import { useEffect, useState } from "react";
import {
    Twitter,
    Facebook,
    Linkedin,
    Share2,
    Copy,
    Check,
    Loader2,
    MessageCircle,
    AtSign,
} from "lucide-react";
import { toast } from "sonner";
import { getBlogSocial, trackShare } from "@/lib/api";

const BTN =
    "inline-flex items-center gap-2 px-3 py-2 border-2 border-white/30 text-white/80 font-mono text-[10px] uppercase tracking-[0.25em] hover:border-white hover:text-white transition-colors";

/**
 * Sticky share rail + AI-generated captions panel.
 * One-tap share to X, FB, LinkedIn, WhatsApp, Reddit.
 * Plus copy-ready captions for IG, TikTok, Reddit posts.
 */
export default function BlogShareBar({ slug, title, accent = "#FF3B30" }) {
    const [copy, setCopy] = useState(null);
    const [openCaptions, setOpenCaptions] = useState(false);
    const [copied, setCopied] = useState("");

    useEffect(() => {
        if (!slug) return;
        getBlogSocial(slug)
            .then(setCopy)
            .catch(() => {});
    }, [slug]);

    const shareUrl =
        typeof window !== "undefined"
            ? `${window.location.origin}/hpm-blog/${slug}`
            : `/hpm-blog/${slug}`;

    const enc = (s) => encodeURIComponent(s || "");
    const tweet = copy?.copy?.tweet || title || "";
    const fbText = copy?.copy?.facebook || title || "";
    const liText = copy?.copy?.linkedin || title || "";
    const wa = copy?.copy?.whatsapp || `${title} → ${shareUrl}`;
    const redditTitle = copy?.copy?.reddit?.title || title || "";

    const openShare = (url, platform) => {
        if (typeof window === "undefined") return;
        if (platform) trackShare(slug, platform);
        window.open(url, "_blank", "noopener,noreferrer,width=620,height=720");
    };

    const onCopy = async (key, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            if (key === "link") trackShare(slug, "copy_link");
            toast.success("Copied — go paste it.");
            setTimeout(() => setCopied(""), 1800);
        } catch {
            toast.error("Copy failed.");
        }
    };

    return (
        <div className="border-2 border-white/20 p-5" data-testid="blog-share-bar">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div
                    className="font-mono text-[10px] uppercase tracking-[0.3em]"
                    style={{ color: accent }}
                >
                    AMPLIFY — REACH ONE MORE PERSON WHO NEEDS THIS
                </div>
                <button
                    onClick={() => setOpenCaptions((v) => !v)}
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-white"
                    data-testid="toggle-captions"
                >
                    {openCaptions ? "HIDE CAPTIONS ↑" : "GET CAPTIONS (IG / TIKTOK / FB) ↓"}
                </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    onClick={() =>
                        openShare(
                            `https://twitter.com/intent/tweet?text=${enc(tweet)}&url=${enc(shareUrl)}`,
                            "twitter",
                        )
                    }
                    className={BTN}
                    data-testid="share-twitter"
                >
                    <Twitter size={12} /> X / TWITTER
                </button>
                <button
                    onClick={() =>
                        openShare(`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, "facebook")
                    }
                    className={BTN}
                    data-testid="share-facebook"
                >
                    <Facebook size={12} /> FACEBOOK
                </button>
                <button
                    onClick={() =>
                        openShare(
                            `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
                            "linkedin",
                        )
                    }
                    className={BTN}
                    data-testid="share-linkedin"
                >
                    <Linkedin size={12} /> LINKEDIN
                </button>
                <button
                    onClick={() => openShare(`https://wa.me/?text=${enc(wa)}`, "whatsapp")}
                    className={BTN}
                    data-testid="share-whatsapp"
                >
                    <MessageCircle size={12} /> WHATSAPP
                </button>
                <button
                    onClick={() =>
                        openShare(
                            `https://www.reddit.com/submit?url=${enc(shareUrl)}&title=${enc(redditTitle)}`,
                            "reddit",
                        )
                    }
                    className={BTN}
                    data-testid="share-reddit"
                >
                    <AtSign size={12} /> REDDIT
                </button>
                <button
                    onClick={() => onCopy("link", shareUrl)}
                    className={BTN}
                    data-testid="share-copy-link"
                >
                    {copied === "link" ? <Check size={12} /> : <Copy size={12} />} COPY LINK
                </button>
            </div>

            {openCaptions && (
                <div className="mt-5 space-y-3" data-testid="captions-panel">
                    {!copy && (
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                            <Loader2 size={12} className="animate-spin" /> LOADING CAPTIONS...
                        </div>
                    )}
                    {copy?.copy && (
                        <>
                            <CaptionRow
                                label="INSTAGRAM"
                                text={copy.copy.instagram}
                                accent={accent}
                                copied={copied === "ig"}
                                onCopy={() => onCopy("ig", copy.copy.instagram)}
                            />
                            <CaptionRow
                                label="TIKTOK"
                                text={copy.copy.tiktok}
                                accent={accent}
                                copied={copied === "tt"}
                                onCopy={() => onCopy("tt", copy.copy.tiktok)}
                            />
                            <CaptionRow
                                label="FACEBOOK"
                                text={fbText}
                                accent={accent}
                                copied={copied === "fb"}
                                onCopy={() => onCopy("fb", fbText)}
                            />
                            <CaptionRow
                                label="LINKEDIN"
                                text={liText}
                                accent={accent}
                                copied={copied === "li"}
                                onCopy={() => onCopy("li", liText)}
                            />
                            <CaptionRow
                                label="TWEET THREAD"
                                text={(copy.copy.tweet_thread || []).join("\n\n")}
                                accent={accent}
                                copied={copied === "th"}
                                onCopy={() =>
                                    onCopy("th", (copy.copy.tweet_thread || []).join("\n\n"))
                                }
                            />
                            <CaptionRow
                                label="REDDIT POST"
                                text={`${copy.copy.reddit?.title || ""}\n\n${copy.copy.reddit?.body || ""}`}
                                accent={accent}
                                copied={copied === "rd"}
                                onCopy={() =>
                                    onCopy(
                                        "rd",
                                        `${copy.copy.reddit?.title || ""}\n\n${copy.copy.reddit?.body || ""}`,
                                    )
                                }
                            />
                            {!copy.enhanced && (
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                                    ✦ AI-POLISHED CAPTIONS COMING ON NEXT RELOAD
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function CaptionRow({ label, text, accent, copied, onCopy }) {
    return (
        <div
            className="border-2 border-white/10 p-3 grid grid-cols-[80px_1fr_auto] gap-3 items-start"
            data-testid={`caption-${label.toLowerCase().replace(/\s/g, "-")}`}
        >
            <div
                className="font-mono text-[10px] uppercase tracking-[0.3em] pt-1"
                style={{ color: accent }}
            >
                {label}
            </div>
            <div className="font-mono text-xs text-white/80 whitespace-pre-wrap break-words">
                {text || "—"}
            </div>
            <button
                onClick={onCopy}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-white border-2 border-white/30 hover:border-white px-2 py-1"
            >
                {copied ? <Check size={11} /> : <Copy size={11} />}
            </button>
        </div>
    );
}
